import PropTypes from 'prop-types';
import React from 'react';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Icon } from '@homeexchange/design';

import {
    addAvailability as addAvailabilityAction,
    fetchAvailabilities as fetchAvailabilitiesAction
} from '../../actions/availabilities';

import i18n from '../../i18n';

import Availability from '../../models/Availability';
import Exchange from '../../models/Exchange';
import User from '../../models/User';
import Conversation from '../../models/Conversation';

import SuccessIcon from '../popup/animations/Success';
import Loading from '../common/Loading';
import Toggle from '../widget/Toggle';
import Utils from '../../utils/utils';

export class PaymentSuccessPopup extends React.PureComponent {
    static propTypes = {
        exchange: PropTypes.instanceOf(Exchange).isRequired,
        user: PropTypes.instanceOf(User),
        show: PropTypes.bool,
        onPopupConfirm: PropTypes.func.isRequired,
        addAvailability: PropTypes.func.isRequired,
        fetchAvailabilities: PropTypes.func.isRequired,
        is3xPaymentFromFinalization: PropTypes.bool,
        conversation: PropTypes.instanceOf(Conversation)
    };

    static defaultProps = {
        show: false
    };

    constructor(props) {
        super(props);

        this.state = {
            checked: true,
            ready: false,
            hasOverlapingAvailabilities: false
        };

        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
    }

    componentDidMount() {
        if (this.props.show) {
            this.checkAvailabilities();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.show && this.props.show) {
            this.checkAvailabilities();
        }
    }

    checkAvailabilities() {
        const { exchange, user, fetchAvailabilities } = this.props;
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();

        if (!exchange) {
            return;
        }

        this.setState({
            ready: false
        });

        // considering only published homes with no available period at the same period
        const homes = user.get('homes').filter((home) => home.isPublished());
        const availabilitiesPromises = [];

        homes.forEach((home) => {
            availabilitiesPromises.push(fetchAvailabilities(home.id));
        });

        Promise.all(availabilitiesPromises).then((availabilities) => {
            let hasOverlapingAvailabilities = false;

            // For each homes, check if the home already has guestwanted period in the calendar
            availabilities.forEach((homeAvailabilities) => {
                homeAvailabilities = homeAvailabilities.data.map(
                    (availability) => new Availability(availability)
                );
                // Only keep overlaping GuestWanted periods
                const period = moment.range(exchange.get('start_on'), exchange.get('end_on'));
                homeAvailabilities = homeAvailabilities.filter(
                    (availability) =>
                        (availability.get('type') === Availability.NON_RECIPROCAL.type ||
                            availability.get('type') === Availability.AVAILABLE.type) &&
                        availability.range().overlaps(period)
                );
                if (homeAvailabilities.length > 0) {
                    hasOverlapingAvailabilities = true;
                }
                // Don't display the toggle to a reciprocal exchange
                if (
                    Availability.RECIPROCAL &&
                    exchangeAsHost &&
                    exchange.get('start_on') === exchangeAsHost.get('start_on') &&
                    exchange.get('end_on') === exchangeAsHost.get('end_on')
                ) {
                    hasOverlapingAvailabilities = true;
                }
            });

            this.setState({
                ready: true,
                hasOverlapingAvailabilities
            });
        });
    }

    onCheckboxChange() {
        this.setState({
            checked: !this.state.checked
        });
    }

    onConfirm() {
        const { addAvailability, user } = this.props;
        const { ready, hasOverlapingAvailabilities, checked } = this.state;

        if (!ready) {
            return;
        }

        if (!hasOverlapingAvailabilities && checked) {
            const { exchange } = this.props;
            const homes = user.get('homes').filter((home) => home.isPublished());
            // create available period on each guest's home calendar
            homes.forEach((home) =>
                addAvailability(home.id, {
                    start_on: exchange.get('start_on'),
                    end_on: exchange.get('end_on'),
                    type: Availability.NON_RECIPROCAL.type
                })
            );
        }

        // Show Hotjar survey
        if (window.hj && typeof hj !== undefined) {
            const locale = i18n.language || 'en';
            hj('trigger', `exchange-finalization-paid-${locale}`);
        }

        this.props.onPopupConfirm();
    }

    render() {
        const { show, exchange, is3xPaymentFromFinalization, user } = this.props;
        const { checked, ready, hasOverlapingAvailabilities } = this.state;

        if (!show) {
            return null;
        }

        const home = exchange.get('home');

        // for 3x payment
        const nbDaysOfEchange = moment(exchange.get('start_on')).diff(moment(), 'days');
        const subscriptionEndDate = this.props.user.get('subscription_end_date')
            ? this.props.user.get('subscription_end_date').format('DD/MM/YYYY')
            : null;
        let paymentSuccessPopupContent = null;

        if (exchange.get('finalized_with_existing_subscription')) {
            // new design referral push don't display sentence when it's not 3xpayment
            paymentSuccessPopupContent = null;
        } else if (is3xPaymentFromFinalization) {
            paymentSuccessPopupContent = i18n.t(
                'billing:payment_success.popup.finalization_3xpayment_content',
                { endDate: subscriptionEndDate }
            );
        } else {
            paymentSuccessPopupContent = i18n.t('billing:payment_success.popup.finalization_content', {
                endDate: subscriptionEndDate
            });
        }

        return (
            <Modal show={show} backdrop="static" className="payment-success-popup">
                <Button
                    aria-label="Close"
                    className="modal-close"
                    data-dismiss="modal"
                    onClick={this.onConfirm}
                >
                    <Icon name="Cross" aria-hidden="true" />
                </Button>
                <Modal.Body>
                    <SuccessIcon />
                    <div className="exchange-finalized-details">
                        <h2>
                            {i18n.t('billing:payment_success.popup.finalization_subtitle', {
                                destination: home.location(),
                                nbDays: nbDaysOfEchange
                            })}
                        </h2>
                        {paymentSuccessPopupContent && <p>{paymentSuccessPopupContent}</p>}
                        {(() => {
                            if (!ready) {
                                return <Loading />;
                            } else if (!hasOverlapingAvailabilities) {
                                return (
                                    <div className="availabilities-details">
                                        <Toggle
                                            id="availabilities-date-toggle"
                                            name="availabilities-date-toggle"
                                            checked={checked}
                                            onChange={this.onCheckboxChange}
                                            text={{
                                                on: i18n.t('common:yes'),
                                                off: i18n.t('common:no')
                                            }}
                                        />
                                        <label
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.t('exchange:payment_success_checkbox', {
                                                    start_on: moment(exchange.get('start_on')).format('L'),
                                                    end_on: moment(exchange.get('end_on')).format('L')
                                                })
                                            }}
                                        />
                                    </div>
                                );
                            }
                        })()}
                    </div>
                    {user && !user.get('is_he_collection') && (
                        <div className="sponsorship-exchange">
                            <div className="sponsorship-icon-text">
                                {user && user.get('is_he_collection') ? (
                                    <img
                                        src={`${Utils.getAssetsDomain()}/images/website/alerts/collection-referral-push.svg`}
                                        alt=""
                                        style={{ width: '8rem', height: '8rem' }}
                                    />
                                ) : (
                                    <img
                                        src={`${Utils.getAssetsDomain()}/images/website/alerts/did_you_know.svg`}
                                        alt=""
                                        style={{ width: '5rem', height: '5rem', marginRight: 16 }}
                                    />
                                )}
                                <div className="sponsorship-text">
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: i18n.t('exchange:sponsorship-text')
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="sponsorship-button">
                                <a href={i18n.t('url:sponsorship')}>
                                    <Button
                                        bsStyle="primary"
                                        bsSize="small"
                                        onClick={this.onConfirm}
                                        disabled={!ready}
                                    >
                                        {ready ? i18n.t('common:invite-friends') : <Loading />}
                                    </Button>
                                </a>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        );
    }
}

const mapStateToProps = (state) => ({
    availabilities: state.availabilities
});

const mapDispatchToProps = (dispatch) => ({
    addAvailability: bindActionCreators(addAvailabilityAction, dispatch),
    fetchAvailabilities: bindActionCreators(fetchAvailabilitiesAction, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSuccessPopup);
